import { Controller } from "@hotwired/stimulus"

// Persisted "user dismissed the install banner" flag. Keyed at module scope so
// the banner stays gone across navigations/reloads until storage is cleared.
const DISMISS_KEY = "pwa-install-dismissed"

// App-wide "install this PWA" banner. Two paths:
//   - Android/Chromium: window stashed the `beforeinstallprompt` event
//     (see application.js); reveal the install button that fires it.
//   - iOS Safari: no programmatic prompt exists; reveal manual
//     Add-to-Home-Screen instructions instead.
// Mobile-only (coarse pointer), never shown when already installed
// (standalone) or after dismissal. Starts hidden; render() reveals a state.
export default class extends Controller {
  static targets = ["install", "ios", "popup"]

  connect() {
    this.standalone =
      matchMedia("(display-mode: standalone)").matches || navigator.standalone === true
    this.coarse = matchMedia("(pointer: coarse)").matches
    this.deferred = window.__deferredInstallPrompt || null

    // Android may fire installable after we connect; re-render when it does.
    this.onInstallable = () => {
      this.deferred = window.__deferredInstallPrompt || null
      this.render()
    }
    window.addEventListener("pwa:installable", this.onInstallable)

    this.render()
  }

  disconnect() {
    window.removeEventListener("pwa:installable", this.onInstallable)
  }

  render() {
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)
    const showIos = this.eligible && isIos && !this.deferred
    const showInstall = this.eligible && !!this.deferred

    if (this.hasIosTarget) this.iosTarget.hidden = !showIos
    if (this.hasInstallTarget) this.installTarget.hidden = !showInstall
    if (this.hasPopupTarget) this.popupTarget.hidden = !(showIos || showInstall)
  }

  // Common gate: mobile, not already installed, not dismissed. Yielding the
  // bottom slot to the higher-priority booking-alerts popup is the banner-slot
  // controller's job, so this only answers "do I have anything to say".
  get eligible() {
    return this.coarse && !this.standalone && !this.dismissed
  }

  async install(event) {
    event.preventDefault()
    if (!this.deferred) return
    this.deferred.prompt()
    const { outcome } = await this.deferred.userChoice
    this.deferred = null
    window.__deferredInstallPrompt = null
    // Whatever the choice, the native prompt won't fire again — don't nag.
    if (outcome === "dismissed") this.persistDismiss()
    this.render()
  }

  dismiss(event) {
    event.preventDefault()
    this.persistDismiss()
    this.render()
  }

  persistDismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, "1")
    } catch {
      // Safari private mode throws on setItem; banner just reappears next load.
    }
  }

  // Safari private mode throws on localStorage access; treat any failure as
  // "not dismissed" so the banner still works, just without persistence.
  get dismissed() {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1"
    } catch {
      return false
    }
  }
}
